// Staff tasks (Workspace → Tasks) strings — EN + TR. Shared by the Admin,
// Seller and Supply shells; each shell only ever sees its own org's tasks.

import type { LocalizedDict } from '../index'

type StaffTasksStrings = {
  navLabel: string
  title: string
  description: string
  newTask: string
  tabs: { mine: string; created: string; all: string }
  filters: {
    searchPlaceholder: string
    status: string
    anyStatus: string
    openOnly: string
    assignee: string
    anyone: string
    overdueOnly: string
  }
  status: { todo: string; in_progress: string; done: string }
  priority: { normal: string; urgent: string }
  columns: { task: string; assignee: string; due: string; status: string }
  unassigned: string
  noDueDate: string
  overdue: string
  comments: (n: number) => string
  empty: { title: string; description: string; filteredTitle: string }
  loadError: string
  form: {
    createTitle: string
    editTitle: string
    titleLabel: string
    titlePlaceholder: string
    descriptionLabel: string
    assigneeLabel: string
    dueLabel: string
    priorityLabel: string
    linkedTo: string
    save: string
    create: string
    cancel: string
    titleRequired: string
  }
  detail: {
    back: string
    edit: string
    delete: string
    deleteConfirmTitle: string
    deleteConfirmBody: string
    deleteConfirm: string
    openedBy: (name: string) => string
    commentsTitle: string
    commentPlaceholder: string
    send: string
    noComments: string
    historyTitle: string
    notFound: string
  }
  history: {
    created: string
    assigned: (name: string) => string
    unassigned: string
    removedPerson: (name: string) => string
    project: (name: string) => string
    projectRemoved: string
    repeat: (rule: string | null) => string
    repeatNext: (date: string) => string
    status: (to: string) => string
    due_date: (to: string | null) => string
    priority: (to: string) => string
    edited: string
    deleted: string
  }
  linked: {
    title: string
    add: string
    empty: string
    kinds: { order: string; product: string; sample: string; work_order: string }
  }
  views: { list: string; calendar: string; month: string; week: string; today: string }
  weekdays: string[]
  buckets: {
    overdue: string
    today: string
    thisWeek: string
    nextWeek: string
    later: string
    noDate: string
    done: string
  }
  calendar: { more: (n: number) => string; addOn: (date: string) => string; prev: string; next: string }
  structure: {
    assignees: string
    addAssignee: string
    removeAssignee: (name: string) => string
    more: (n: number) => string
    subtasks: string
    subtaskProgress: (done: number, total: number) => string
    addSubtask: string
    subtaskPlaceholder: string
    removeSubtask: (title: string) => string
    files: string
    fileCount: (n: number) => string
  }
  composer: {
    bold: string
    italic: string
    list: string
    link: string
    mention: string
    noMatch: string
    hint: string
    sendHint: (shortcut: string) => string
  }
  projects: {
    tab: string
    newProject: string
    fromTemplate: string
    columns: { project: string; owner: string; status: string; progress: string; target: string }
    status: { on_track: string; at_risk: string; off_track: string; on_hold: string; done: string }
    stageLabel: (name: string) => string
    overdueTasks: (n: number) => string
    progress: (done: number, total: number) => string
    tasksDone: (done: number, total: number) => string
    noOwner: string
    noTarget: string
    empty: { title: string; description: string }
    form: {
      createTitle: string
      editTitle: string
      nameLabel: string
      namePlaceholder: string
      descriptionLabel: string
      ownerLabel: string
      statusLabel: string
      startLabel: string
      targetLabel: string
      stagesLabel: string
      stagesHelp: string
      templateLabel: string
      noTemplate: string
      templateHelp: (stages: number, tasks: number) => string
      nameRequired: string
    }
    detail: {
      back: string
      edit: string
      delete: string
      saveTemplate: string
      templateName: string
      templateSaved: string
      deleteConfirmTitle: string
      deleteConfirmBody: string
      noStage: string
      addTask: string
      addStage: string
      stagePlaceholder: string
      renameStage: (name: string) => string
      moveUp: (name: string) => string
      moveDown: (name: string) => string
      removeStage: (name: string) => string
      removeStageConfirm: (name: string) => string
      noTasks: string
      notFound: string
      ownerLine: (name: string) => string
      targetLine: (date: string) => string
    }
    taskField: { project: string; stage: string; none: string }
    templates: {
      title: string
      remove: (name: string) => string
      empty: string
      size: (stages: number, tasks: number) => string
    }
    toasts: { created: string; saved: string; deleted: string }
  }
  table: {
    columnsMenu: string
    columns: {
      project: string
      stage: string
      priority: string
      subtasks: string
      createdBy: string
      createdAt: string
    }
    sortBy: (column: string) => string
    editAssigneesTitle: string
    editAssigneesFor: (task: string) => string
  }
  sidebar: {
    label: string
    myTasks: string
    allTasks: string
    createdByMe: string
    allProjects: string
    folders: string
    addFolder: string
    folderPlaceholder: string
    noFolder: string
    renameFolder: (name: string) => string
    removeFolder: (name: string) => string
    removeFolderConfirm: (name: string) => string
    people: string
    other: string
    automations: string
    personTitle: (name: string) => string
    counts: (open: number, late: number) => string
    jumpTo: string
  }
  folderField: { label: string; none: string }
  privacy: {
    label: string
    help: string
    members: string
    membersHelp: string
    addMember: string
    onlyCreator: string
    badge: string
    lockTitle: (count: number) => string
    deleteBody: string
    notMember: string
    memberHasTasks: string
    noTemplate: string
  }
  repeat: {
    label: string
    none: string
    untilLabel: string
    rules: { daily: string; weekdays: string; weekly: string; monthly: string }
    badge: (rule: string) => string
  }
  automations: {
    tab: string
    title: string
    description: string
    add: string
    empty: string
    kinds: { done_notify: string; overdue_notify: string; project_assign: string }
    targets: { opener: string; project_owner: string; user: string }
    sentence: {
      done_notify: (scope: string, who: string) => string
      overdue_notify: (scope: string, who: string) => string
      project_assign: (scope: string, who: string) => string
    }
    anyProject: string
    inProject: (name: string, stage: string | null) => string
    ran: (n: number, when: string | null) => string
    neverRan: string
    toggle: (on: boolean) => string
    remove: string
    form: {
      title: string
      kindLabel: string
      projectLabel: string
      anyProject: string
      stageLabel: string
      anyStage: string
      targetLabel: string
      personLabel: string
      save: string
      projectRequired: string
      personRequired: string
    }
    toasts: { created: string; deleted: string }
  }
  notes: {
    title: string
    add: string
    empty: string
    items: (open: number, total: number) => string
    form: {
      createTitle: string
      editTitle: string
      titleLabel: string
      titlePlaceholder: string
      dateLabel: string
      attendeesLabel: string
      bodyLabel: string
      itemsLabel: string
      itemPlaceholder: string
      addItem: string
      itemPerson: string
      itemDate: string
      save: string
    }
    toTask: string
    becameTask: string
    removeItem: (text: string) => string
    delete: string
    deleteConfirm: string
    fromNote: string
    toasts: { saved: string; deleted: string; taskCreated: string }
  }
  toasts: {
    created: string
    saved: string
    deleted: string
    commented: string
    error: string
    notInTeam: string
    markedDone: string
    statusSaved: string
  }
  quick: {
    addLabel: string
    addPlaceholder: string
    add: string
    done: string
    markDone: (title: string) => string
    loading: string
    retry: string
    toggleGroup: (group: string) => string
  }
}

export const staffTasksI18n: LocalizedDict<StaffTasksStrings> = {
  en: {
    navLabel: 'Tasks',
    title: 'Tasks',
    description: "Your team's to-do list. Only your team can see these tasks.",
    newTask: 'New task',
    tabs: { mine: 'Assigned to me', created: 'Opened by me', all: 'All team tasks' },
    filters: {
      searchPlaceholder: 'Search tasks',
      status: 'Status',
      anyStatus: 'Any status',
      openOnly: 'Open',
      assignee: 'Assignee',
      anyone: 'Anyone',
      overdueOnly: 'Overdue only',
    },
    status: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
    priority: { normal: 'Normal', urgent: 'Urgent' },
    columns: { task: 'Task', assignee: 'Assignee', due: 'Due', status: 'Status' },
    unassigned: 'Unassigned',
    noDueDate: 'No date',
    overdue: 'Overdue',
    comments: (n) => (n === 1 ? '1 comment' : `${n} comments`),
    empty: {
      title: 'No tasks yet',
      description: 'Open a task and assign it to someone on your team.',
      filteredTitle: 'No tasks match these filters',
    },
    loadError: 'Tasks could not be loaded. Try again in a moment.',
    form: {
      createTitle: 'New task',
      editTitle: 'Edit task',
      titleLabel: 'Title',
      titlePlaceholder: 'What needs to be done?',
      descriptionLabel: 'Details',
      assigneeLabel: 'Assign to',
      dueLabel: 'Due date',
      priorityLabel: 'Priority',
      linkedTo: 'Linked to',
      save: 'Save',
      create: 'Create task',
      cancel: 'Cancel',
      titleRequired: 'Give the task a title.',
    },
    detail: {
      back: 'All tasks',
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete this task?',
      deleteConfirmBody: 'The task and its comments disappear from the list.',
      deleteConfirm: 'Delete task',
      openedBy: (name) => `Opened by ${name}`,
      commentsTitle: 'Comments',
      commentPlaceholder: 'Write a comment',
      send: 'Send',
      noComments: 'No comments yet.',
      historyTitle: 'History',
      notFound: 'This task does not exist or was deleted.',
    },
    history: {
      created: 'opened the task',
      assigned: (name) => `assigned it to ${name}`,
      unassigned: 'removed the assignee',
      removedPerson: (name) => `removed ${name}`,
      project: (name) => `moved it to the project ${name}`,
      projectRemoved: 'took it out of its project',
      repeat: (rule) => (rule ? `set it to repeat ${rule.toLowerCase()}` : 'stopped it repeating'),
      repeatNext: (date) => `opened the next one for ${date}`,
      status: (to) => `moved it to ${to}`,
      due_date: (to) => (to ? `set the due date to ${to}` : 'removed the due date'),
      priority: (to) => `set priority to ${to}`,
      edited: 'edited the task',
      deleted: 'deleted the task',
    },
    linked: {
      title: 'Tasks',
      add: 'Task',
      empty: 'No tasks for this record.',
      kinds: { order: 'Order', product: 'Product', sample: 'Sample', work_order: 'Work order' },
    },
    views: { list: 'List', calendar: 'Calendar', month: 'Month', week: 'Week', today: 'Today' },
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    buckets: {
      overdue: 'Overdue',
      today: 'Today',
      thisWeek: 'This week',
      nextWeek: 'Next week',
      later: 'Later',
      noDate: 'No date',
      done: 'Done',
    },
    calendar: {
      more: (n) => `+${n} more`,
      addOn: (date) => `New task on ${date}`,
      prev: 'Previous',
      next: 'Next',
    },
    structure: {
      assignees: 'Assignees',
      addAssignee: 'Add person',
      removeAssignee: (name) => `Remove ${name}`,
      more: (n) => `+${n}`,
      subtasks: 'Sub-tasks',
      subtaskProgress: (done, total) => `${done} / ${total}`,
      addSubtask: 'Add',
      subtaskPlaceholder: 'Add a sub-task',
      removeSubtask: (title) => `Delete ${title}`,
      files: 'Files',
      fileCount: (n) => (n === 1 ? '1 file' : `${n} files`),
    },
    composer: {
      bold: 'Bold',
      italic: 'Italic',
      list: 'Bulleted list',
      link: 'Link',
      mention: 'Mention someone',
      noMatch: 'No one on your team by that name',
      hint: 'Type @ to mention a teammate',
      sendHint: (shortcut) => `${shortcut} to send`,
    },
    projects: {
      tab: 'Projects',
      newProject: 'New project',
      fromTemplate: 'From template',
      columns: { project: 'Project', owner: 'Owner', status: 'Status', progress: 'Progress', target: 'Target date' },
      status: { on_track: 'On track', at_risk: 'At risk', off_track: 'Off track', on_hold: 'On hold', done: 'Done' },
      stageLabel: (name) => `Stage: ${name}`,
      overdueTasks: (n) => (n === 1 ? '1 overdue task' : `${n} overdue tasks`),
      progress: (done, total) => `${done}/${total}`,
      tasksDone: (done, total) => `${done} of ${total} tasks done`,
      noOwner: 'No owner',
      noTarget: 'No target date',
      empty: {
        title: 'No projects yet',
        description: 'Group related tasks into stages — start blank or from a template.',
      },
      form: {
        createTitle: 'New project',
        editTitle: 'Edit project',
        nameLabel: 'Name',
        namePlaceholder: 'What is this project about?',
        descriptionLabel: 'Details',
        ownerLabel: 'Owner',
        statusLabel: 'Status',
        startLabel: 'Start date',
        targetLabel: 'Target date',
        stagesLabel: 'Stages',
        stagesHelp: 'One stage per line, in order. You can change them later.',
        templateLabel: 'Template',
        noTemplate: 'Blank project',
        templateHelp: (stages, tasks) => `${stages} stages, ${tasks} tasks — due dates count from the start date.`,
        nameRequired: 'Give the project a name.',
      },
      detail: {
        back: 'All projects',
        edit: 'Edit',
        delete: 'Delete',
        saveTemplate: 'Save as template',
        templateName: 'Template name',
        templateSaved: 'Template saved',
        deleteConfirmTitle: 'Delete this project?',
        deleteConfirmBody: 'Its tasks are kept; they just leave the project.',
        noStage: 'No stage',
        addTask: 'Add a task to this stage',
        addStage: 'Add stage',
        stagePlaceholder: 'New stage name',
        renameStage: (name) => `Rename ${name}`,
        moveUp: (name) => `Move ${name} up`,
        moveDown: (name) => `Move ${name} down`,
        removeStage: (name) => `Remove ${name}`,
        removeStageConfirm: (name) => `Remove the stage "${name}"? Its tasks stay in the project.`,
        noTasks: 'No tasks in this stage yet.',
        notFound: 'This project does not exist or was deleted.',
        ownerLine: (name) => `Owner: ${name}`,
        targetLine: (date) => `Target ${date}`,
      },
      taskField: { project: 'Project', stage: 'Stage', none: 'None' },
      templates: {
        title: 'Templates',
        remove: (name) => `Delete template ${name}`,
        empty: 'No templates yet.',
        size: (stages, tasks) => `${stages} stages, ${tasks} tasks`,
      },
      toasts: { created: 'Project created', saved: 'Project saved', deleted: 'Project deleted' },
    },
    table: {
      columnsMenu: 'Columns',
      columns: {
        project: 'Project',
        stage: 'Stage',
        priority: 'Priority',
        subtasks: 'Sub-tasks',
        createdBy: 'Opened by',
        createdAt: 'Opened on',
      },
      sortBy: (column) => `Sort by ${column}`,
      editAssigneesTitle: 'Edit assignees',
      editAssigneesFor: (task) => `Edit assignees for ${task}`,
    },
    sidebar: {
      label: 'Tasks menu',
      myTasks: 'My tasks',
      allTasks: 'All team tasks',
      createdByMe: 'Opened by me',
      allProjects: 'All projects',
      folders: 'Folders',
      addFolder: 'Add folder',
      folderPlaceholder: 'Folder name',
      noFolder: 'No folder',
      renameFolder: (name) => `Rename ${name}`,
      removeFolder: (name) => `Delete ${name}`,
      removeFolderConfirm: (name) => `Delete the folder "${name}"? Its projects stay, without a folder.`,
      people: 'People',
      other: 'More',
      automations: 'Automations',
      personTitle: (name) => `${name}'s tasks`,
      counts: (open, late) => (late > 0 ? `${open} open, ${late} overdue` : `${open} open`),
      jumpTo: 'Go to',
    },
    folderField: { label: 'Folder', none: 'No folder' },
    privacy: {
      label: 'Private project',
      help: 'Only the people you pick see this project and its tasks — nobody else in the team, the account owner included.',
      members: 'Who sees it',
      membersHelp: 'You are always one of them. Its tasks can only go to these people.',
      addMember: 'Add a person',
      onlyCreator: 'Only the person who created the project changes who sees it.',
      badge: 'Private',
      lockTitle: (count) => `Private — ${count} ${count === 1 ? 'person sees' : 'people see'} it`,
      deleteBody:
        'The project and all of its tasks will be deleted. Its tasks do not become team tasks.',
      notMember: 'Only the project’s members can get its tasks.',
      memberHasTasks: 'Someone you left out still owns the project or has tasks in it — reassign them first.',
      noTemplate: 'A private project cannot be saved as a template.',
    },
    repeat: {
      label: 'Repeat',
      none: 'Does not repeat',
      untilLabel: 'Repeat until',
      rules: { daily: 'Every day', weekdays: 'Every weekday', weekly: 'Every week', monthly: 'Every month' },
      badge: (rule) => `↻ ${rule}`,
    },
    automations: {
      tab: 'Automations',
      title: 'Automations',
      description: 'Simple rules that run on their own. Pick a kind, fill in the blanks.',
      add: 'Add rule',
      empty: 'No rules yet.',
      kinds: {
        done_notify: 'When a task is done, notify…',
        overdue_notify: 'When a task is overdue, also notify…',
        project_assign: 'Tasks added to a project go to…',
      },
      targets: { opener: 'the person who opened it', project_owner: 'the project owner', user: 'a person' },
      sentence: {
        done_notify: (scope, who) => `When a task${scope} is done → notify ${who}`,
        overdue_notify: (scope, who) => `When a task${scope} is overdue → also notify ${who}`,
        project_assign: (scope, who) => `A task added${scope} with nobody on it → assign to ${who}`,
      },
      anyProject: '',
      inProject: (name, stage) => (stage ? ` in ${name} · ${stage}` : ` in ${name}`),
      ran: (n, when) => (when ? `Ran ${n} times · last ${when}` : `Ran ${n} times`),
      neverRan: 'Has not run yet',
      toggle: (on) => (on ? 'Turn off' : 'Turn on'),
      remove: 'Delete rule',
      form: {
        title: 'New rule',
        kindLabel: 'Rule',
        projectLabel: 'Project',
        anyProject: 'Any task',
        stageLabel: 'Stage',
        anyStage: 'Any stage',
        targetLabel: 'Who',
        personLabel: 'Person',
        save: 'Add rule',
        projectRequired: 'Pick the project.',
        personRequired: 'Pick the person.',
      },
      toasts: { created: 'Rule added', deleted: 'Rule deleted' },
    },
    notes: {
      title: 'Meeting notes',
      add: 'New note',
      empty: 'No meeting notes yet.',
      items: (open, total) => (total === 0 ? 'No action items' : `${open} of ${total} action items not a task yet`),
      form: {
        createTitle: 'New meeting note',
        editTitle: 'Meeting note',
        titleLabel: 'Title',
        titlePlaceholder: 'e.g. Weekly ops meeting',
        dateLabel: 'Meeting date',
        attendeesLabel: 'Attendees',
        bodyLabel: 'Notes',
        itemsLabel: 'Action items',
        itemPlaceholder: 'Something to do',
        addItem: 'Add',
        itemPerson: 'Who',
        itemDate: 'By',
        save: 'Save note',
      },
      toTask: 'Turn into task',
      becameTask: 'Task opened',
      removeItem: (text) => `Remove ${text}`,
      delete: 'Delete note',
      deleteConfirm: 'Delete this note? Tasks made from it stay.',
      fromNote: 'From meeting note',
      toasts: { saved: 'Note saved', deleted: 'Note deleted', taskCreated: 'Task opened' },
    },
    toasts: {
      created: 'Task created',
      saved: 'Task saved',
      deleted: 'Task deleted',
      commented: 'Comment added',
      error: 'Something went wrong. Try again.',
      notInTeam: 'That person is not on your team.',
      markedDone: 'Task marked done',
      statusSaved: 'Status updated',
    },
    quick: {
      addLabel: 'New task title',
      addPlaceholder: 'Add a task, press Enter',
      add: 'Add task',
      done: 'Done',
      markDone: (title) => `Mark done: ${title}`,
      loading: 'Loading tasks',
      retry: 'Try again',
      toggleGroup: (group) => `Show or hide ${group}`,
    },
  },
  tr: {
    navLabel: 'Görevler',
    title: 'Görevler',
    description: 'Ekibinin yapılacaklar listesi. Bu görevleri yalnız senin ekibin görür.',
    newTask: 'Yeni görev',
    tabs: { mine: 'Bana atananlar', created: 'Benim açtıklarım', all: 'Ekibin tüm görevleri' },
    filters: {
      searchPlaceholder: 'Görev ara',
      status: 'Durum',
      anyStatus: 'Tüm durumlar',
      openOnly: 'Açık',
      assignee: 'Atanan',
      anyone: 'Herkes',
      overdueOnly: 'Yalnız gecikenler',
    },
    status: { todo: 'Yapılacak', in_progress: 'Yapılıyor', done: 'Bitti' },
    priority: { normal: 'Normal', urgent: 'Acil' },
    columns: { task: 'Görev', assignee: 'Atanan', due: 'Son tarih', status: 'Durum' },
    unassigned: 'Atanmadı',
    noDueDate: 'Tarih yok',
    overdue: 'Gecikti',
    comments: (n) => `${n} yorum`,
    empty: {
      title: 'Henüz görev yok',
      description: 'Bir görev aç ve ekibinden birine ata.',
      filteredTitle: 'Bu filtrelere uyan görev yok',
    },
    loadError: 'Görevler yüklenemedi. Birazdan tekrar dene.',
    form: {
      createTitle: 'Yeni görev',
      editTitle: 'Görevi düzenle',
      titleLabel: 'Başlık',
      titlePlaceholder: 'Ne yapılması gerekiyor?',
      descriptionLabel: 'Ayrıntılar',
      assigneeLabel: 'Kime',
      dueLabel: 'Son tarih',
      priorityLabel: 'Öncelik',
      linkedTo: 'Bağlı kayıt',
      save: 'Kaydet',
      create: 'Görevi oluştur',
      cancel: 'Vazgeç',
      titleRequired: 'Göreve bir başlık ver.',
    },
    detail: {
      back: 'Tüm görevler',
      edit: 'Düzenle',
      delete: 'Sil',
      deleteConfirmTitle: 'Bu görev silinsin mi?',
      deleteConfirmBody: 'Görev ve yorumları listeden kalkar.',
      deleteConfirm: 'Görevi sil',
      openedBy: (name) => `Açan: ${name}`,
      commentsTitle: 'Yorumlar',
      commentPlaceholder: 'Yorum yaz',
      send: 'Gönder',
      noComments: 'Henüz yorum yok.',
      historyTitle: 'Geçmiş',
      notFound: 'Bu görev yok ya da silinmiş.',
    },
    history: {
      created: 'görevi açtı',
      assigned: (name) => `${name} kişisine atadı`,
      unassigned: 'atamayı kaldırdı',
      removedPerson: (name) => `${name} kişisini çıkardı`,
      project: (name) => `${name} projesine taşıdı`,
      projectRemoved: 'projeden çıkardı',
      repeat: (rule) => (rule ? `tekrarını "${rule}" yaptı` : 'tekrarını kapattı'),
      repeatNext: (date) => `bir sonrakini ${date} için açtı`,
      status: (to) => `durumu ${to} yaptı`,
      due_date: (to) => (to ? `son tarihi ${to} yaptı` : 'son tarihi kaldırdı'),
      priority: (to) => `önceliği ${to} yaptı`,
      edited: 'görevi düzenledi',
      deleted: 'görevi sildi',
    },
    linked: {
      title: 'Görevler',
      add: 'Görev',
      empty: 'Bu kayda bağlı görev yok.',
      kinds: { order: 'Sipariş', product: 'Ürün', sample: 'Numune', work_order: 'İş emri' },
    },
    views: { list: 'Liste', calendar: 'Takvim', month: 'Ay', week: 'Hafta', today: 'Bugün' },
    weekdays: ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'],
    buckets: {
      overdue: 'Gecikmiş',
      today: 'Bugün',
      thisWeek: 'Bu hafta',
      nextWeek: 'Gelecek hafta',
      later: 'Sonra',
      noDate: 'Tarihsiz',
      done: 'Bitti',
    },
    calendar: {
      more: (n) => `+${n} daha`,
      addOn: (date) => `${date} tarihli yeni görev`,
      prev: 'Önceki',
      next: 'Sonraki',
    },
    structure: {
      assignees: 'Sorumlular',
      addAssignee: 'Kişi ekle',
      removeAssignee: (name) => `${name} kişisini çıkar`,
      more: (n) => `+${n}`,
      subtasks: 'Alt görevler',
      subtaskProgress: (done, total) => `${done} / ${total}`,
      addSubtask: 'Ekle',
      subtaskPlaceholder: 'Alt görev ekle',
      removeSubtask: (title) => `${title} sil`,
      files: 'Dosyalar',
      fileCount: (n) => `${n} dosya`,
    },
    composer: {
      bold: 'Kalın',
      italic: 'İtalik',
      list: 'Madde listesi',
      link: 'Bağlantı',
      mention: 'Birinden bahset',
      noMatch: 'Ekibinde bu isimde kimse yok',
      hint: 'Ekip arkadaşından bahsetmek için @ yaz',
      sendHint: (shortcut) => `Göndermek için ${shortcut}`,
    },
    projects: {
      tab: 'Projeler',
      newProject: 'Yeni proje',
      fromTemplate: 'Şablondan proje',
      columns: { project: 'Proje', owner: 'Sorumlu', status: 'Durum', progress: 'İlerleme', target: 'Hedef tarih' },
      status: { on_track: 'Yolunda', at_risk: 'Riskte', off_track: 'Geride', on_hold: 'Beklemede', done: 'Tamamlandı' },
      stageLabel: (name) => `Aşama: ${name}`,
      overdueTasks: (n) => `${n} gecikmiş görev`,
      progress: (done, total) => `${done}/${total}`,
      tasksDone: (done, total) => `${total} görevden ${done} tanesi bitti`,
      noOwner: 'Sorumlu yok',
      noTarget: 'Hedef tarih yok',
      empty: {
        title: 'Henüz proje yok',
        description: 'İlgili görevleri aşamalara ayır — boş başla ya da şablondan kur.',
      },
      form: {
        createTitle: 'Yeni proje',
        editTitle: 'Projeyi düzenle',
        nameLabel: 'Ad',
        namePlaceholder: 'Bu proje ne hakkında?',
        descriptionLabel: 'Ayrıntılar',
        ownerLabel: 'Sorumlu',
        statusLabel: 'Durum',
        startLabel: 'Başlangıç tarihi',
        targetLabel: 'Hedef tarih',
        stagesLabel: 'Aşamalar',
        stagesHelp: 'Her satıra bir aşama, sırasıyla. Sonra değiştirebilirsin.',
        templateLabel: 'Şablon',
        noTemplate: 'Boş proje',
        templateHelp: (stages, tasks) => `${stages} aşama, ${tasks} görev — son tarihler başlangıç tarihinden sayılır.`,
        nameRequired: 'Projeye bir ad ver.',
      },
      detail: {
        back: 'Tüm projeler',
        edit: 'Düzenle',
        delete: 'Sil',
        saveTemplate: 'Şablon olarak kaydet',
        templateName: 'Şablon adı',
        templateSaved: 'Şablon kaydedildi',
        deleteConfirmTitle: 'Bu proje silinsin mi?',
        deleteConfirmBody: 'Görevleri silinmez; yalnızca projeden çıkar.',
        noStage: 'Aşamasız',
        addTask: 'Bu aşamaya görev ekle',
        addStage: 'Aşama ekle',
        stagePlaceholder: 'Yeni aşama adı',
        renameStage: (name) => `${name} aşamasını yeniden adlandır`,
        moveUp: (name) => `${name} aşamasını yukarı taşı`,
        moveDown: (name) => `${name} aşamasını aşağı taşı`,
        removeStage: (name) => `${name} aşamasını kaldır`,
        removeStageConfirm: (name) => `"${name}" aşaması kaldırılsın mı? Görevleri projede kalır.`,
        noTasks: 'Bu aşamada henüz görev yok.',
        notFound: 'Bu proje yok ya da silinmiş.',
        ownerLine: (name) => `Sorumlu: ${name}`,
        targetLine: (date) => `Hedef ${date}`,
      },
      taskField: { project: 'Proje', stage: 'Aşama', none: 'Yok' },
      templates: {
        title: 'Şablonlar',
        remove: (name) => `${name} şablonunu sil`,
        empty: 'Henüz şablon yok.',
        size: (stages, tasks) => `${stages} aşama, ${tasks} görev`,
      },
      toasts: { created: 'Proje oluşturuldu', saved: 'Proje kaydedildi', deleted: 'Proje silindi' },
    },
    table: {
      columnsMenu: 'Sütunlar',
      columns: {
        project: 'Proje',
        stage: 'Aşama',
        priority: 'Öncelik',
        subtasks: 'Alt görevler',
        createdBy: 'Açan',
        createdAt: 'Açılış tarihi',
      },
      sortBy: (column) => `${column} sütununa göre sırala`,
      editAssigneesTitle: 'Atananları düzenle',
      editAssigneesFor: (task) => `${task} için atananları düzenle`,
    },
    sidebar: {
      label: 'Görevler menüsü',
      myTasks: 'Görevlerim',
      allTasks: 'Ekibin tüm görevleri',
      createdByMe: 'Benim açtıklarım',
      allProjects: 'Tüm projeler',
      folders: 'Klasörler',
      addFolder: 'Klasör ekle',
      folderPlaceholder: 'Klasör adı',
      noFolder: 'Klasörsüz',
      renameFolder: (name) => `${name} klasörünü yeniden adlandır`,
      removeFolder: (name) => `${name} klasörünü sil`,
      removeFolderConfirm: (name) => `"${name}" klasörü silinsin mi? Projeleri silinmez, klasörsüz kalır.`,
      people: 'Kişiler',
      other: 'Diğer',
      automations: 'Otomasyonlar',
      personTitle: (name) => `${name} — görevler`,
      counts: (open, late) => (late > 0 ? `${open} açık, ${late} gecikmiş` : `${open} açık`),
      jumpTo: 'Git',
    },
    folderField: { label: 'Klasör', none: 'Klasörsüz' },
    privacy: {
      label: 'Özel proje',
      help: 'Bu projeyi ve görevlerini yalnız seçtiğin kişiler görür — ekipte başka kimse, hesap sahibi de dahil, göremez.',
      members: 'Kimler görsün',
      membersHelp: 'Sen her zaman içindesin. Görevleri yalnız bu kişilere verilebilir.',
      addMember: 'Kişi ekle',
      onlyCreator: 'Kimlerin göreceğini yalnız projeyi açan kişi değiştirir.',
      badge: 'Özel',
      lockTitle: (count) => `Özel — ${count} kişi görüyor`,
      deleteBody: 'Proje ve tüm görevleri silinecek. Görevleri ekip görevine dönüşmez.',
      notMember: 'Bu projenin görevleri yalnız üyelerine verilebilir.',
      memberHasTasks: 'Çıkardığın kişi hâlâ projenin sahibi ya da projede görevi var — önce görevleri başkasına ver.',
      noTemplate: 'Özel proje şablon olarak kaydedilemez.',
    },
    repeat: {
      label: 'Tekrarla',
      none: 'Tekrarlanmaz',
      untilLabel: 'Şu tarihe kadar',
      rules: { daily: 'Her gün', weekdays: 'Hafta içi her gün', weekly: 'Her hafta', monthly: 'Her ay' },
      badge: (rule) => `↻ ${rule}`,
    },
    automations: {
      tab: 'Otomasyonlar',
      title: 'Otomasyonlar',
      description: 'Kendiliğinden çalışan basit kurallar. Türünü seç, boşlukları doldur.',
      add: 'Kural ekle',
      empty: 'Henüz kural yok.',
      kinds: {
        done_notify: 'Görev bitince şu kişiye bildir…',
        overdue_notify: 'Görev gecikince şu kişiye de bildir…',
        project_assign: 'Projeye eklenen görevi şu kişiye ata…',
      },
      targets: { opener: 'görevi açan kişi', project_owner: 'proje sorumlusu', user: 'bir kişi' },
      sentence: {
        done_notify: (scope, who) => `${scope}Görev bitince → ${who} bilgilendirilir`,
        overdue_notify: (scope, who) => `${scope}Görev gecikince → ${who} da bilgilendirilir`,
        project_assign: (scope, who) => `${scope}Sorumlusuz eklenen görev → ${who} kişisine atanır`,
      },
      anyProject: '',
      inProject: (name, stage) => (stage ? `${name} · ${stage}: ` : `${name}: `),
      ran: (n, when) => (when ? `${n} kez çalıştı · son ${when}` : `${n} kez çalıştı`),
      neverRan: 'Henüz çalışmadı',
      toggle: (on) => (on ? 'Kapat' : 'Aç'),
      remove: 'Kuralı sil',
      form: {
        title: 'Yeni kural',
        kindLabel: 'Kural',
        projectLabel: 'Proje',
        anyProject: 'Tüm görevler',
        stageLabel: 'Aşama',
        anyStage: 'Tüm aşamalar',
        targetLabel: 'Kime',
        personLabel: 'Kişi',
        save: 'Kuralı ekle',
        projectRequired: 'Projeyi seç.',
        personRequired: 'Kişiyi seç.',
      },
      toasts: { created: 'Kural eklendi', deleted: 'Kural silindi' },
    },
    notes: {
      title: 'Toplantı notları',
      add: 'Yeni not',
      empty: 'Henüz toplantı notu yok.',
      items: (open, total) => (total === 0 ? 'Yapılacak yok' : `${total} yapılacaktan ${open} tanesi henüz görev değil`),
      form: {
        createTitle: 'Yeni toplantı notu',
        editTitle: 'Toplantı notu',
        titleLabel: 'Başlık',
        titlePlaceholder: 'ör. Haftalık operasyon toplantısı',
        dateLabel: 'Toplantı tarihi',
        attendeesLabel: 'Katılanlar',
        bodyLabel: 'Konuşulanlar',
        itemsLabel: 'Yapılacaklar',
        itemPlaceholder: 'Yapılacak bir iş',
        addItem: 'Ekle',
        itemPerson: 'Kim',
        itemDate: 'Ne zamana',
        save: 'Notu kaydet',
      },
      toTask: 'Göreve çevir',
      becameTask: 'Görev açıldı',
      removeItem: (text) => `${text} maddesini kaldır`,
      delete: 'Notu sil',
      deleteConfirm: 'Bu not silinsin mi? Nottan açılan görevler kalır.',
      fromNote: 'Toplantı notundan',
      toasts: { saved: 'Not kaydedildi', deleted: 'Not silindi', taskCreated: 'Görev açıldı' },
    },
    toasts: {
      created: 'Görev oluşturuldu',
      saved: 'Görev kaydedildi',
      deleted: 'Görev silindi',
      commented: 'Yorum eklendi',
      error: 'Bir şeyler ters gitti. Tekrar dene.',
      notInTeam: 'Bu kişi ekibinde değil.',
      markedDone: 'Görev tamamlandı',
      statusSaved: 'Durum güncellendi',
    },
    quick: {
      addLabel: 'Yeni görev başlığı',
      addPlaceholder: 'Görev ekle, Enter’a bas',
      add: 'Görev ekle',
      done: 'Bitti',
      markDone: (title) => `Bitti olarak işaretle: ${title}`,
      loading: 'Görevler yükleniyor',
      retry: 'Tekrar dene',
      toggleGroup: (group) => `${group} grubunu aç veya kapat`,
    },
  },
}
